import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  emailAccount,
  firebaseReady,
  resetAccountPassword,
  signInWithProvider,
} from "../services/firebaseAuth";

const SignInPage: React.FC = () => {
  const [register, setRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const finish = () => navigate(params.get("returnTo") || "/");
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      finish();
    } catch {
      setError(
        "We could not complete that sign-in. Check your details and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "70vh",
        display: "grid",
        placeItems: "center",
        padding: 22,
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(() => emailAccount(email, password, register));
        }}
        style={{
          width: "min(440px,100%)",
          padding: 38,
          border: "1px solid rgba(23,35,30,.13)",
          borderRadius: 24,
          background: "#fffdf8",
          boxShadow: "0 22px 70px rgba(41,55,48,.11)",
        }}
      >
        <span className="section-kicker">Your Observer account</span>
        <h1
          style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: 36,
            letterSpacing: "-.055em",
            margin: "12px 0 8px",
          }}
        >
          {register ? "Create your account." : "Welcome in."}
        </h1>
        <p style={{ color: "#66736d", fontSize: 13, lineHeight: 1.6 }}>
          Verify your email to review a teacher, reader, coach, psychic, medium,
          astrologer, or spiritual practitioner—or to manage your relationship
          with a profile.
        </p>
        {!firebaseReady && (
          <p style={messageStyle}>
            Account services are being provisioned. The public directory remains
            available.
          </p>
        )}
        {error && (
          <p
            style={{ ...messageStyle, color: "#9f2f2f", background: "#fff0ed" }}
          >
            {error}
          </p>
        )}
        {notice && <p style={messageStyle}>{notice}</p>}
        <button
          type="button"
          disabled={busy || !firebaseReady}
          onClick={() => run(() => signInWithProvider("google"))}
          className="button button-ink"
          style={providerButton}
        >
          Continue with Google
        </button>
        <button
          type="button"
          disabled={busy || !firebaseReady}
          onClick={() => run(() => signInWithProvider("apple"))}
          className="button"
          style={{
            ...providerButton,
            border: "1px solid rgba(23,35,30,.13)",
            marginTop: 10,
            background: "#fff",
          }}
        >
          Continue with Apple
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "23px 0",
            color: "#89938e",
            fontSize: 10,
          }}
        >
          <span style={rule} />
          OR
          <span style={rule} />
        </div>
        <label style={label}>
          Email address
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            autoComplete="email"
            required
            style={input}
          />
        </label>
        <label style={label}>
          Password
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            minLength={8}
            autoComplete={register ? "new-password" : "current-password"}
            required
            style={input}
          />
        </label>
        <button
          disabled={busy || !firebaseReady}
          className="button button-primary"
          style={{ width: "100%", border: 0, marginTop: 14 }}
        >
          {busy
            ? "One moment…"
            : register
              ? "Create account"
              : "Continue with email"}
        </button>
        {!register && (
          <button
            type="button"
            disabled={!email || busy || !firebaseReady}
            onClick={async () => {
              setError("");
              try {
                await resetAccountPassword(email);
                setNotice(
                  "If that account exists, a reset link has been sent.",
                );
              } catch {
                setError("Enter your email, then try the reset again.");
              }
            }}
            style={textButton}
          >
            Forgot password?
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setRegister(!register);
            setError("");
            setNotice("");
          }}
          style={textButton}
        >
          {register
            ? "Already have an account? Sign in"
            : "New to Observer Theory? Create an account"}
        </button>
        <p
          style={{
            marginTop: 20,
            color: "#7a8580",
            fontSize: 10,
            lineHeight: 1.6,
          }}
        >
          By continuing, you accept the <Link to="/terms">Terms</Link> and{" "}
          <Link to="/privacy">Privacy Policy</Link>. YouTube, Apple, and Google
          accounts are never used to manufacture or influence reviews.
        </p>
      </form>
    </div>
  );
};

const providerButton: React.CSSProperties = {
  width: "100%",
  border: 0,
  marginTop: 18,
};
const input: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  marginTop: 7,
  padding: "14px 15px",
  border: "1px solid rgba(23,35,30,.13)",
  borderRadius: 11,
  background: "#fff",
};
const label: React.CSSProperties = {
  display: "block",
  marginTop: 12,
  color: "#425049",
  fontSize: 12,
  fontWeight: 700,
};
const rule: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: "rgba(23,35,30,.13)",
};
const messageStyle: React.CSSProperties = {
  padding: 11,
  borderRadius: 10,
  background: "#eef4ed",
  color: "#315d4d",
  fontSize: 12,
};
const textButton: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 13,
  padding: 0,
  border: 0,
  background: "transparent",
  color: "#315d4d",
  cursor: "pointer",
  fontSize: 12,
};

export default SignInPage;
